//Proofed
import FilterListIcon from '@mui/icons-material/FilterList'
import ManageSearchIcon from '@mui/icons-material/ManageSearch'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import BarChartIcon from '@mui/icons-material/BarChart'
import SearchIcon from '@mui/icons-material/Search'
import InsightsIcon from '@mui/icons-material/Insights'
import type { HowItWorksStep } from '../components/HowItWorksSection'

// Hero right-panel highlights
export const efficiencyHighlights = [
    'Identify and qualify opportunities faster with better market intelligence',
    'Reduce time-to-first-draft with AI bid writing built on your documents',
    'Use analytics to understand buyer behaviour before you write a word',
    'Track competitor activity and market trends to sharpen your positioning',
    'Measure and improve your BD performance with data, not gut feel',
]

// Challenge cards  section 2
export const efficiencyChallenges = [
    {
        heading: "You're spending too much time qualifying opportunities that don't convert",
        body: "Without robust buyer intelligence and market data, qualification relies on instinct and optimism. The result is time invested in bids you were unlikely to win at the expense of bids you could have",
    },
    {
        heading: "Your bid process is slower and more variable than it needs to be",
        body: "First drafts take too long. Quality varies between writers. Review cycles are inconsistent. Every inefficiency in your bid process compounds across a growing pipeline. The cost, in time and lost opportunity, is significant",
    },
    {
        heading: "You're making BD decisions based on experience rather than market data",
        body: "Experience is valuable, but it's not a substitute for data. Without analytics on buyer behaviour, competitor activity and market trends, your strategy is reactive rather than predictive, giving competitors with better intelligence a systematic advantage",
    },
]

// Recommended solution stack  section 3
export const efficiencySolutionStack = [
    {
        Icon: SearchIcon,
        title: 'CFP Core Pro',
        body: 'Complete tender coverage with smart filtering, buyer database, competitor tracking and framework intelligence. Qualify faster, pursue smarter. One platform for every aspect of opportunity management',
        tag: 'Platform',
        link: '/products/core',
        linkLabel: 'Learn about Core Pro',
    },
    {
        Icon: AutoAwesomeIcon,
        title: 'Tender Ninja',
        body: 'AI-assisted bid writing using your own documents. Cut drafting time significantly, improve response quality and analyse your bids against buyer criteria before submission. Faster, stronger, more consistent',
        tag: 'Platform Add-on',
        link: '/products/tender-ninja',
        linkLabel: 'Learn about Tender Ninja',
    },
    {
        Icon: InsightsIcon,
        title: 'CFP Analytics',
        body: '8 specialist dashboards covering buyer behaviour, supplier activity, market trends, regional patterns and KPI evaluation. Make strategy decisions with data, not gut feel',
        tag: 'Platform',
        link: '/products/analytics',
        linkLabel: 'Learn about Analytics',
    },
]

// Journey steps  section 4
export const efficiencySteps: HowItWorksStep[] = [
    {
        num: '1',
        Icon: FilterListIcon,
        title: 'Sharpen Your Monitoring',
        body: 'Configure Core Pro with precise filters so your team only sees relevant opportunities. Less noise, faster review, better qualification decisions from day one',
    },
    {
        num: '2',
        Icon: ManageSearchIcon,
        title: 'Qualify with Intelligence',
        body: 'Use buyer history, competitor data and award intelligence to make bid/no-bid decisions with confidence. Stop investing in opportunities you\'re unlikely to win',
    },
    {
        num: '3',
        Icon: AutoAwesomeIcon,
        title: 'Write and Evaluate Faster',
        body: 'Use Tender Ninja to produce stronger first drafts faster, then evaluate your response against the buyer\'s criteria before submission. Better bids in less time',
    },
    {
        num: '4',
        Icon: BarChartIcon,
        title: 'Measure and Improve',
        body: 'Use CFP Analytics to track market trends, buyer activity and your BD performance over time. Systematic improvement driven by data, not anecdote',
    },
]

// Outcomes checklist  section 5
export const efficiencyOutcomes = [
    'Spend less time monitoring and qualifying. The platform helps you identify opportunities fast',
    'Produce stronger bid responses faster and evaluate them against buyer criteria before submission',
    'Make bid/no-bid decisions based on buyer intelligence and competitor data, not gut feel',
    'Identify market trends and upcoming renewals before your competitors do',
    'Track your performance over time and systematically improve your win rate',
]

// Self-qualification checklist  section 6
export const efficiencyQualifiers = [
    'You\'re actively bidding on UK or Irish public sector contracts and want better results',
    'Your win rate is lower than you\'d like and you\'re not always sure why',
    'Your bid process is slower than it needs to be and first drafts take too long',
    'You want better market intelligence to inform your BD strategy, not just more tenders',
    'You\'re ready to invest in the full suite to get a meaningful competitive advantage',
]



// FAQs  section 8
export const efficiencyFaqs = [
    {
        q: 'What\'s the difference between Core Pro Basic, Professional and Enterprise?',
        a: "Core Pro Basic provides fundamental tender monitoring and alert functionality for smaller teams. Professional adds the full buyer database, competitor tracking, framework intelligence and advanced search capabilities. Enterprise includes everything in Professional plus dedicated account management, custom integrations and volume licence pricing for larger teams. Our team will recommend the right tier based on your team size and usage needs.",
    },
    {
        q: 'How much time does Tender Ninja actually save?',
        a: "Based on client feedback, Tender Ninja typically reduces first-draft time by 50-70% compared to writing from a blank page. The actual saving depends on bid complexity, the quality and size of your document library and how well it's been organised. Clients with comprehensive, well-structured document libraries see the largest time savings. The pre-submission evaluation feature also reduces review cycle time by identifying gaps and weaknesses before the response leaves your team.",
    },
    {
        q: 'What insights does CFP Analytics provide?',
        a: "CFP Analytics includes 8 specialist dashboards: Buyer Activity (which buyers are procuring and when), Supplier Performance (what competitors are winning and at what value), Market Trends (sector-level spending patterns over time), Regional Analysis (geographic distribution of procurement activity), Framework Intelligence (which frameworks are most active in your sectors), Value Segmentation (contract size distribution), Renewal Forecasting (contracts coming up for re-tender) and KPI Evaluation (your own performance metrics and trends).",
    },
    {
        q: 'Can I trial the full suite before committing?',
        a: "Yes. CFP Core Pro includes a 14-day free trial with full access to platform features. Tender Ninja and Analytics can be included in an extended trial for qualifying businesses. Book a discovery call and we'll set up a trial configuration based on your specific sector and requirements, so you can evaluate the tools against your actual workflow.",
    },
    {
        q: 'How long before I see an improvement in win rate?',
        a: "Most clients see process improvements immediately: faster opportunity identification, better qualification decisions, reduced drafting time. Win rate improvements typically appear within 2-4 bid cycles, as the quality and targeting of submissions improves. Because public sector procurement cycles can be 3-6 months from submission to award, measurable win rate data usually becomes visible 4-6 months after adoption. The Analytics KPI dashboard tracks this progression.",
    },
    {
        q: 'Does the platform integrate with our existing CRM?',
        a: "CFP Core Pro supports data export in standard formats compatible with most CRM systems. Tender Ninja operates as a standalone tool but can export bid content and evaluation reports in formats suitable for your document management system.",
    },
]

// Related solution links  section 9
export const efficiencyRelated = [
    {
        headline: 'Expand into New Sectors',
        body: 'Performing well and ready to grow into adjacent sectors? Dedicated licences, AI bid writing and expert review to scale your public sector operation across multiple markets',
        link: '/solutions/expand-into-new-sectors',
        tag: 'Expanding Reach',
    },
    {
        headline: 'Find a Dedicated Bid Partner',
        body: 'Want to hand your BD to experts entirely? Our fully managed service handles strategy, monitoring and bid writing so you can focus on delivery',
        link: '/solutions/find-a-dedicated-bid-partner',
        tag: 'Execution Support',
    },
]
